import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { BaseModalProps } from './Modal';
import { FormErrorAlert, cn } from '..';
import { LoadingState } from './LoadingState';
import { useEffect } from 'react';
import { useToast } from './hooks/use-toast';

export function SheetModal({
  open,
  onClose,
  title,
  subTitle,
  isLoading,
  children,
  showSuccess,
  successMessage,
  errorMessage,
  primaryButton,
  modal = true,
  className,
  testId,
  closeButtonTestId,
  contentTestId,
}: Omit<BaseModalProps, 'width' | 'stickyFooter'> & {
  testId?: string;
  closeButtonTestId?: string;
  contentTestId?: string;
}) {
  const { toast } = useToast();

  useEffect(() => {
    if (showSuccess) {
      toast({
        title: `Success!`,
        description: successMessage,
        variant: 'default',
        className: '-translate-y-5 border-green-500 bg-green-50 text-green-800',
      });
      onClose();
    }
  }, [showSuccess, successMessage]);

  return (
    <Sheet
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose();
      }}
      modal={modal}
    >
      <SheetContent
        data-testid={contentTestId || testId}
        className={cn(
          'flex flex-col pb-0',
          'max-h-[100vh] overflow-auto',
          'min-w-[95%] sm:min-w-[80%] md:min-w-[500px]',
          showSuccess ? 'border-t-4 border-green-600' : '',
          className,
        )}
        contentTestId={contentTestId}
        closeButtonTestId={closeButtonTestId}
      >
        <div className={cn('flex-1', !showSuccess ? 'block' : 'hidden')}>
          <SheetHeader className="text-left">
            <SheetTitle className=" text-primary-dark text-2xl !font-medium">{title}</SheetTitle>
            {subTitle && (
              <SheetDescription className="text-sm text-gray-700 md:text-base">
                {subTitle}
              </SheetDescription>
            )}
          </SheetHeader>

          {children}
        </div>

        <LoadingState isLoading={isLoading} />

        {errorMessage && (
          <FormErrorAlert
            mainErrorMessage={errorMessage}
            className="mt-4"
          />
        )}

        {primaryButton && !showSuccess && (
          <SheetFooter
            className={cn(
              'mt-auto flex-col sm:flex-row sm:justify-start',
              'sticky bottom-0 z-50 -mx-6 w-[calc(100%+3rem)] bg-white px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:py-6',
            )}
          >
            {primaryButton}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
